import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaLinkedin, FaTwitter, FaFacebook, FaInstagram, FaSendBackward } from 'react-icons/fa';
import SEOHead from './SEOHead';
import { submitContactForm } from '../repository/api';
import { useToast, ToastWrapper } from './Toast';
import Toast from './Toast';

const Contact = () => {
  const [visibleElements, setVisibleElements] = useState({});
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    service: 'Select a service'
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  
  // Toast notifications
  const { toasts, showSuccess, showError, removeToast } = useToast();

  const sectionRef = useRef(null);
  const elementsRef = useRef({
    header: null,
    form: null,
    info: null,
    shapes: []
  });

  // Handle scroll-based animations with Intersection Observer
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-5% 0px',
      threshold: 0.1
    };

    const handleIntersection = (entries) => {
      entries.forEach(entry => {
        setVisibleElements(prev => ({
          ...prev,
          [entry.target.id]: entry.isIntersecting
        }));
      });
    };

    const observer = new IntersectionObserver(handleIntersection, observerOptions);

    Object.values(elementsRef.current).forEach(ref => {
      if (Array.isArray(ref)) {
        ref.forEach(item => {
          if (item) observer.observe(item);
        });
      } else {
        if (ref) observer.observe(ref);
      }
    });

    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear errors when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validate = () => {
    const errors = {};

    if (!formState.name.trim()) errors.name = "Name is required";

    if (!formState.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formState.email)) {
      errors.email = "Valid email is required";
    }

    if (!formState.message.trim()) errors.message = "Please enter your message";

    if (formState.service === 'Select a service') errors.service = "Please select a service";

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validate();
    setFormErrors(errors);

    if (Object.keys(errors).length === 0) {
      setIsSubmitting(true);

      try {
        // Prepare data for API
        const contactData = {
          full_name: formState.name,
          email: formState.email,
          phone_number: formState.phone || undefined,
          company: formState.company || undefined,
          service_type: formState.service,
          message: formState.message
        };

        // Submit to API
        const response = await submitContactForm(contactData);
        
        // Success
        setIsSubmitting(false);
        setSubmitSuccess(true);
        showSuccess('Message sent successfully! We\'ll get back to you soon.');
        
        // Reset form
        setFormState({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: '',
          service: 'Select a service'
        });

        // Reset success message after 5 seconds
        setTimeout(() => {
          setSubmitSuccess(false);
        }, 5000);

      } catch (error) {
        setIsSubmitting(false);
        console.error('Contact form submission error:', error);
        showError(error.message || 'Failed to send message. Please try again.');
      }
    }
  };

  // Background shape data
  const shapes = [
    { className: "circle", color: "#105090", top: "15%", right: "5%", size: "120px", id: "contact-shape-1" },
    { className: "triangle", color: "#2090B0", bottom: "20%", left: "8%", size: "100px", id: "contact-shape-2" },
    { className: "square", color: "#105090", top: "70%", right: "15%", size: "80px", id: "contact-shape-3" },
    { className: "hexagon", color: "#2090B0", top: "30%", left: "12%", size: "90px", id: "contact-shape-4" }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact Codewave",
    "description": "Get in touch with Codewave for mobile payment solutions, bulk SMS services, and API integration. Contact our expert team for support and inquiries.",
    "url": "https://codewave.com/contact"
  };

  return (
    <>
      <SEOHead
        title="Contact Codewave - Get Mobile Payment Solutions Support"
        description="Get in touch with Codewave for mobile payment solutions, bulk SMS services, and API integration. Contact our expert team for support and inquiries."
        keywords="contact Codewave, mobile payment support, SMS services support, API integration support, Kenya fintech contact"
        url="https://codewave.com/contact"
        canonical="https://codewave.com/contact"
        structuredData={structuredData}
      />
      <ContactSection id="contact" ref={sectionRef}>
        <BackgroundShapes>
          {shapes.map((shape, index) => (
            <GeometricShape
              key={shape.id}
              id={shape.id}
              ref={el => elementsRef.current.shapes[index] = el}
              className={shape.className}
              color={shape.color}
              top={shape.top}
              left={shape.left}
              right={shape.right}
              bottom={shape.bottom}
              size={shape.size}
              isVisible={visibleElements[shape.id]}
            />
          ))}
        </BackgroundShapes>

        <Container>
          <SectionHeader
            id="contact-header"
            ref={el => elementsRef.current.header = el}
            isVisible={visibleElements["contact-header"]}
          >
            <SectionTitle>Get in <HighlightText>Touch</HighlightText></SectionTitle>
            <SectionDescription>
              Ready to transform your communication strategy? Contact us today!
            </SectionDescription>
          </SectionHeader>

          <ContactContent>
            <ContactFormContainer
              id="contact-form"
              ref={el => elementsRef.current.form = el}
              isVisible={visibleElements["contact-form"]}
            >
              {submitSuccess ? (
                <SuccessMessage>
                  <SuccessIcon>✓</SuccessIcon>
                  <h3>Thank You!</h3>
                  <p>Your message has been sent successfully. We'll get back to you within 24 hours.</p>
                  <ResetButton onClick={() => setSubmitSuccess(false)}>Send another message</ResetButton>
                </SuccessMessage>
              ) : (
                <Form onSubmit={handleSubmit}>
                  <FormTitle>Send us a message</FormTitle>

                  <FormRow>
                    <FormGroup>
                      <Label htmlFor="name">Full Name*</Label>
                      <Input
                        type="text"
                        id="name"
                        name="name"
                        value={formState.name}
                        onChange={handleChange}
                        hasError={!!formErrors.name}
                        placeholder="John Doe"
                      />
                      {formErrors.name && <ErrorText>{formErrors.name}</ErrorText>}
                    </FormGroup>

                    <FormGroup>
                      <Label htmlFor="email">Email*</Label>
                      <Input
                        type="email"
                        id="email"
                        name="email"
                        value={formState.email}
                        onChange={handleChange}
                        hasError={!!formErrors.email}
                        placeholder="john@example.com"
                      />
                      {formErrors.email && <ErrorText>{formErrors.email}</ErrorText>}
                    </FormGroup>
                  </FormRow>

                  <FormRow>
                    <FormGroup>
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formState.phone}
                        onChange={handleChange}
                        placeholder="+254 700 000000"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label htmlFor="company">Company</Label>
                      <Input
                        type="text"
                        id="company"
                        name="company"
                        value={formState.company}
                        onChange={handleChange}
                        placeholder="Your company name"
                      />
                    </FormGroup>
                  </FormRow>

                  <FormGroup>
                    <Label htmlFor="service">Service You're Interested In*</Label>
                    <Select
                      id="service"
                      name="service"
                      value={formState.service}
                      onChange={handleChange}
                      hasError={!!formErrors.service}
                    >
                      <option disabled>Select a service</option>
                      <option>Bulk SMS</option>
                      <option>USSD Services</option>
                      <option>Call Center Solutions</option>
                      <option>Payment Solutions</option>
                      <option>Software Development</option>
                      <option>Other</option>
                    </Select>
                    {formErrors.service && <ErrorText>{formErrors.service}</ErrorText>}
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="message">Your Message*</Label>
                    <TextArea
                      id="message"
                      name="message"
                      value={formState.message}
                      onChange={handleChange}
                      hasError={!!formErrors.message}
                      placeholder="Tell us about your project or requirements..."
                      rows="5"
                    ></TextArea>
                    {formErrors.message && <ErrorText>{formErrors.message}</ErrorText>}
                  </FormGroup>

                  <SubmitButton type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </SubmitButton>
                </Form>
              )}
            </ContactFormContainer>

            <ContactInfoContainer
              id="contact-info"
              ref={el => elementsRef.current.info = el}
              isVisible={visibleElements["contact-info"]}
            >
              <InfoCard>
                <InfoTitle>Contact Information</InfoTitle>
                <InfoText>
                  Reach out to us directly or visit our office. We're always happy to hear from you!
                </InfoText>

                <ContactDetailsList>
                  <ContactDetail>
                    <DetailIcon><FaPhone /></DetailIcon>
                    <DetailContent>
                      <DetailLabel>Phone</DetailLabel>
                      <DetailValue>+254 703 012100</DetailValue>
                    </DetailContent>
                  </ContactDetail>

                  <ContactDetail>
                    <DetailIcon><FaEnvelope /></DetailIcon>
                    <DetailContent>
                      <DetailLabel>Email</DetailLabel>
                      <DetailValue>marketing@codewave.co.ke</DetailValue>
                    </DetailContent>
                  </ContactDetail>

                  <ContactDetail>
                    <DetailIcon><FaMapMarkerAlt /></DetailIcon>
                    <DetailContent>
                      <DetailLabel>Office</DetailLabel>
                      <DetailValue>Red Commercial Complex, Nairobi, Kenya</DetailValue>
                    </DetailContent>
                  </ContactDetail>
                </ContactDetailsList>

                <MapContainer>
                  <ResponsiveIframe
                    src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d15945.393276302454!2d36.89532!3d-1.33617!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x182f1b159c645d2b%3A0x0!2zMSAzMdCsMjAnMzkuOSJTIDM2wrA1Myc0Mi44IkU!5e0!3m2!1sen!2ske!4v1699840000000"
                    allowFullScreen=""
                    loading="lazy"
                    title="Codewave Office Location"
                  />
                  <OpenMapButton
                    href="https://www.google.com/maps?q=Red+Commercial+Complex,+Mombasa+Road,+Nairobi"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open in Google Maps
                  </OpenMapButton>
                </MapContainer>

                <SocialLinks>
                  <SocialLink href="https://linkedin.com" target="_blank" rel="noopener noreferrer">
                    <FaLinkedin />
                  </SocialLink>
                  <SocialLink href="https://twitter.com" target="_blank" rel="noopener noreferrer">
                    <FaTwitter />
                  </SocialLink>
                  <SocialLink href="https://facebook.com" target="_blank" rel="noopener noreferrer">
                    <FaFacebook />
                  </SocialLink>
                  <SocialLink href="https://instagram.com" target="_blank" rel="noopener noreferrer">
                    <FaInstagram />
                  </SocialLink>
                </SocialLinks>

                <ResponseTime>
                  <ResponseIcon>⚡</ResponseIcon>
                  <ResponseText>We typically respond within 24 hours</ResponseText>
                </ResponseTime>
              </InfoCard>
            </ContactInfoContainer>
          </ContactContent>
        </Container>
      </ContactSection>
      
      {/* Toast Container */}
      <ToastWrapper position="top-right">
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </ToastWrapper>
    </>
  );
};

export default Contact;

// === Animations ===
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const gradientAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const floatAnimation = keyframes`
  0% { transform: translate(0, 0) rotate(0deg); }
  50% { transform: translate(0, -8px) rotate(2deg); }
  100% { transform: translate(0, 0) rotate(0deg); }
`;

const pulse = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(32, 144, 176, 0.4); }
  70% { transform: scale(1.05); box-shadow: 0 0 0 10px rgba(32, 144, 176, 0); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(32, 144, 176, 0); }
`;

// === Styled Components ===
const ContactSection = styled.section`
  padding: 100px 40px;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #f8f9fb 0%, #f1f4f9 100%);
  min-height: 90vh;
  display: flex;
  justify-content: center;
  
  @media (max-width: 992px) {
    padding: 80px 30px;
  }
  
  @media (max-width: 576px) {
    padding: 60px 20px;
  }
`;

const BackgroundShapes = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  z-index: 1;
`;

const GeometricShape = styled.div`
  position: absolute;
  top: ${props => props.top || 'auto'};
  left: ${props => props.left || 'auto'};
  right: ${props => props.right || 'auto'};
  bottom: ${props => props.bottom || 'auto'};
  width: ${props => props.size};
  height: ${props => props.size};
  background-color: ${props => props.color};
  opacity: ${props => props.isVisible ? 0.05 : 0};
  z-index: 0;
  transform: ${props => props.isVisible ? 'scale(1)' : 'scale(0.8)'};
  transition: opacity 0.3s ease, transform 0.3s ease;
  animation: ${props => props.isVisible ? floatAnimation : 'none'} 6s infinite ease-in-out;
  
  &.circle {
    border-radius: 50%;
  }
  
  &.square {
    transform: ${props => props.isVisible ? 'rotate(45deg)' : 'rotate(0deg) scale(0.8)'};
    transition: opacity 0.3s ease, transform 0.3s ease;
  }
  
  &.triangle {
    clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
  }
  
  &.hexagon {
    clip-path: polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%);
  }
`;

const Container = styled.div`
  max-width: 1200px;
  width: 100%;
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 50px;
  width: 100%;
  opacity: ${props => props.isVisible ? 1 : 0};
  transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(20px)'};
  transition: opacity 0.3s ease-out, transform 0.3s ease-out;
`;

const SectionTitle = styled.h2`
  font-size: 42px;
  font-weight: 700;
  margin-bottom: 20px;
  text-align: center;
  position: relative;
  z-index: 2;
  color: #105090;
  
  @media (max-width: 768px) {
    font-size: 36px;
  }
  
  @media (max-width: 576px) {
    font-size: 30px;
  }
`;

const HighlightText = styled.span`
  background: linear-gradient(90deg, #105090, #2090B0);
  background-size: 200% auto;
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
  animation: ${gradientAnimation} 4s ease infinite;
`;

const SectionDescription = styled.p`
  font-size: 18px;
  line-height: 1.6;
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
  color: #666;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ContactContent = styled.div`
  display: flex;
  width: 100%;
  gap: 30px;
  
  @media (max-width: 992px) {
    flex-direction: column-reverse;
  }
`;

// Form styling
const ContactFormContainer = styled.div`
  flex: 1.5;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  opacity: ${props => props.isVisible ? 1 : 0};
  transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(20px)'};
  transition: opacity 0.4s ease, transform 0.4s ease;
  
  @media (max-width: 992px) {
    margin-top: 30px;
  }
`;

const Form = styled.form`
  padding: 30px;
  
  @media (max-width: 576px) {
    padding: 20px;
  }
`;

const FormTitle = styled.h3`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 25px;
  color: #105090;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, #105090, #2090B0);
  }
  
  @media (max-width: 576px) {
    font-size: 22px;
  }
`;

const FormRow = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 0;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  flex: 1;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #555;
  font-size: 15px;
`;

const inputStyle = `
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 15px;
  transition: all 0.3s ease;
  color: #333;
  background: #f9f9f9;
  
  &:focus {
    border-color: #2090B0;
    background: white;
    outline: none;
    box-shadow: 0 0 0 3px rgba(32, 144, 176, 0.1);
  }
  
  &::placeholder {
    color: #aaa;
  }
`;

const Input = styled.input`
  ${inputStyle}
  border-color: ${props => props.hasError ? '#ff4d4f' : '#e0e0e0'};
  
  &:focus {
    border-color: ${props => props.hasError ? '#ff4d4f' : '#2090B0'};
    box-shadow: 0 0 0 3px ${props => props.hasError ? 'rgba(255, 77, 79, 0.1)' : 'rgba(32, 144, 176, 0.1)'};
  }
`;

const TextArea = styled.textarea`
  ${inputStyle}
  resize: vertical;
  min-height: 120px;
  border-color: ${props => props.hasError ? '#ff4d4f' : '#e0e0e0'};
  
  &:focus {
    border-color: ${props => props.hasError ? '#ff4d4f' : '#2090B0'};
    box-shadow: 0 0 0 3px ${props => props.hasError ? 'rgba(255, 77, 79, 0.1)' : 'rgba(32, 144, 176, 0.1)'};
  }
`;

const Select = styled.select`
  ${inputStyle}
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23C5337B' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 15px center;
  background-size: 15px;
  padding-right: 45px;
  border-color: ${props => props.hasError ? '#ff4d4f' : '#e0e0e0'};
  
  &:focus {
    border-color: ${props => props.hasError ? '#ff4d4f' : '#2090B0'};
    box-shadow: 0 0 0 3px ${props => props.hasError ? 'rgba(255, 77, 79, 0.1)' : 'rgba(32, 144, 176, 0.1)'};
  }
  
  option {
    color: #333;
    background: white;
  }
`;

const ErrorText = styled.div`
  color: #ff4d4f;
  font-size: 13px;
  margin-top: 5px;
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, #105090, #2090B0);
  color: white;
  font-size: 16px;
  font-weight: 600;
  padding: 14px 30px;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-block;
  margin-top: 15px;
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.2);
    transition: all 0.4s ease;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
    
    &:before {
      left: 100%;
    }
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
    
    &:hover:before {
      left: -100%;
    }
  }
`;

const SuccessMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 50px 30px;
  
  h3 {
    color: #105090;
    font-size: 28px;
    margin: 20px 0 15px;
  }
  
  p {
    color: #666;
    font-size: 16px;
    max-width: 400px;
    margin-bottom: 30px;
  }
`;

const SuccessIcon = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: #2090B0;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  animation: ${pulse} 2s infinite;
`;

const ResetButton = styled.button`
  background: transparent;
  color: #105090;
  font-size: 15px;
  font-weight: 600;
  padding: 10px 20px;
  border: 2px solid #105090;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #105090;
    color: white;
  }
`;

// Contact info styling
const ContactInfoContainer = styled.div`
  flex: 1;
  opacity: ${props => props.isVisible ? 1 : 0};
  transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(20px)'};
  transition: opacity 0.4s ease 0.1s, transform 0.4s ease 0.1s;
`;

const InfoCard = styled.div`
  background: linear-gradient(135deg, #105090 0%, #1a2540 100%);
  border-radius: 16px;
  color: white;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  height: 100%;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 992px) {
    padding: 25px;
  }
  
  @media (max-width: 576px) {
    padding: 20px;
  }
`;

const InfoTitle = styled.h3`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 15px;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 40px;
    height: 3px;
    background: #2090B0;
  }
  
  @media (max-width: 576px) {
    font-size: 22px;
  }
`;

const InfoText = styled.p`
  font-size: 15px;
  line-height: 1.6;
  margin-bottom: 25px;
  color: rgba(255, 255, 255, 0.8);
`;

const ContactDetailsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 25px;
`;

const ContactDetail = styled.div`
  display: flex;
  align-items: flex-start;
`;

const DetailIcon = styled.div`
  margin-right: 15px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #2090B0;
  flex-shrink: 0;
`;

const DetailContent = styled.div`
  flex: 1;
`;

const DetailLabel = styled.h4`
  margin: 0 0 5px;
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
`;

const DetailValue = styled.p`
  margin: 0;
  font-size: 15px;
  color: white;
`;

const MapContainer = styled.div`
  margin: 20px 0;
  border-radius: 10px;
  overflow: hidden;
  position: relative;  /* Needed for absolute positioning of button */
  height: 0;
  padding-bottom: 56.25%; /* 16:9 aspect ratio for responsive iframe */
`;
const ResponsiveIframe = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
  border-radius: 10px;
`;

const OpenMapButton = styled.a`
  position: absolute;
  bottom: 10px;
  right: 10px;
  background: rgba(255, 255, 255, 0.9);
  padding: 8px 14px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 14px;
  text-decoration: none;
  color: #333;
  box-shadow: 0 2px 6px rgba(0,0,0,0.2);
  transition: background 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 1);
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 12px;
  margin-top: auto;
  padding-top: 20px;
`;

const SocialLink = styled.a`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: all 0.3s ease;
  
  &:hover {
    background: #2090B0;
    transform: translateY(-3px);
  }
`;

const ResponseTime = styled.div`
  display: flex;
  align-items: center;
  margin-top: 20px;
  padding: 10px 15px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
`;

const ResponseIcon = styled.div`
  font-size: 20px;
  margin-right: 10px;
`;

const ResponseText = styled.p`
  margin: 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
`;
